import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import StopIcon from '@assets/img/icons/stop.svg?react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';
import { LightColorEffect } from '@/components/common/LightColorEffect/LightColorEffect';
import { H1, StringSmall } from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { useAppSelector } from '@/redux/store';

import { GameItemsRewards } from '../GameItemsRewards/GameItemsRewards';
import { useCardsGameCollectRewards } from '../hooks/collect-rewards';
import { Reward } from '../Reward/Reward';

import css from './styles.module.scss';

interface GameOverEventProps {
  isGameOverEventActive: boolean;
  closeGameOverEvent: () => void;
}

const GameOverEvent: FC<GameOverEventProps> = ({
  isGameOverEventActive,
  closeGameOverEvent,
}) => {
  const { collectRewards } = useCardsGameCollectRewards();
  const { t } = useTranslation();
  const rewardCollected = useAppSelector(
    (state) => state.diceGame.miniGame?.rewardCollected,
  );

  const goToMainBoard = (): void => {
    collectRewards();
    closeGameOverEvent();
  };

  return (
    <AnimatePresence>
      {isGameOverEventActive && (
        <motion.div
          className={css.gameOverEvent}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="w-full flex flex-col items-center relative z-[1]">
            <H1
              weight="extrabold"
              className={cn('mb-2 !text-[#FFE6E6]', css.title)}
            >
              {t('Game over!')}
            </H1>
            <StringSmall className="!text-white/80 text-center mb-11 max-w-[8rem]">
              {t("You've reached \n the end of this run...")}
            </StringSmall>
          </div>
          <div className="relative w-24 h-24">
            <StopIcon className="w-full h-full relative z-[2]" />
            <LightColorEffect
              lightClassName={css.greyGradient}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85.04%]"
            />
          </div>
          {rewardCollected && (
            <>
              {rewardCollected.cash !== undefined ? (
                <Reward
                  kind="Cash"
                  value={rewardCollected.cash || 0}
                  className="mb-11 mt-4"
                />
              ) : (
                <GameItemsRewards
                  className="!mb-11 !mt-4"
                  list={Object.entries(rewardCollected || {}).map(
                    ([item, value]) => ({
                      itemName: item as
                        | ResourceTypeText
                        | PlantTypeText
                        | GoodsType,
                      value,
                    }),
                  )}
                />
              )}
            </>
          )}
          <div className="flex flex-col items-center w-full relative z-[1]">
            <StringSmall weight="regular" className="mb-6 !text-white/80">
              {t('...claim and return to the main board')}
            </StringSmall>
            <div className="w-[12.125rem] px-4 flex items-center gap-2 mb-[7.75rem]">
              <MainButton onClick={goToMainBoard} text={t('Claim')} />
            </div>
            <div className="w-16 h-16 shrink-0" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { GameOverEvent };
